<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import SplitButton from 'primevue/splitbutton'
import type { Quality } from '@/types/supplier-types'
import type { ReturnedRoll } from '@/types/batch-types'
import FinishedEditRollListItem from './FinishedEditRollListItem.vue'
import { getRollById } from '@/api/rolls'

const props = withDefaults(
  defineProps<{
    quality?: Quality
    includeLength?: boolean
    includeMergeNo?: boolean
    includeSupplierNo?: boolean
    allowAddRolls?: boolean
    lengthLabel?: string
    lengthUnitLabel?: string
    weightLabel?: string
    disabled?: boolean
    includeInactiveRolls?: boolean
    showActions?: boolean
    currentBatchNumber?: string
  }>(),
  {
    includeLength: true,
    includeMergeNo: true,
    includeSupplierNo: false,
    allowAddRolls: false,
    lengthLabel: 'Length',
    lengthUnitLabel: 'Unit',
    weightLabel: 'Weight',
    disabled: true,
    includeInactiveRolls: true,
    showActions: true
  }
)

const model = defineModel<ReturnedRoll[]>()

const isTrulyMergedBatch = computed(() => {
  if (!model.value || model.value.length === 0) return false

  const originalBatchNumbers = new Set(
      model.value
          .map(roll => roll.originalBatchNumber)
          .filter(batchNumber => batchNumber && batchNumber.trim() !== '')
  )

  return originalBatchNumbers.size > 1
})

const fetchOriginalBatchInfo = async () => {
  if (!model.value) return

  for (const roll of model.value) {
    if (roll.previousRollId && !roll.originalBatchNumber) {
      try {
        const originalRoll = await getRollById(roll.previousRollId)
        if (originalRoll) {
          roll.originalBatchNumber = originalRoll.originalBatchNumber}
      } catch (error) {
        console.error(`Error fetching original roll data for ${roll.previousRollId}:`, error)
      }
    }
  }
}

const removeRoll = (rollIndex: number) => {
  if (!model.value) return

  const roll = model.value[rollIndex]
  const rollToRemoveIndex = model.value.findIndex((r) => r === roll)
  model.value?.splice(rollToRemoveIndex, 1)
}

const addRolls = (numRolls: number) => {
  model.value = model.value || []

  const newRolls = Array.from({ length: numRolls }, () => ({
    rollNumber: '',
    newWeight: 0,
    newLengthValue: 0,
    newLengthUnit: 0
  }))

  model.value.push(...newRolls)
}

const addRoll = () => addRolls(1)

const addRollOptions = [
  {
    label: 'Add 2 more Rolls',
    command: () => addRolls(2)
  },
  {
    label: 'Add 3 more Rolls',
    command: () => addRolls(3)
  },
  {
    label: 'Add 4 more Rolls',
    command: () => addRolls(4)
  }
]

watch(model, fetchOriginalBatchInfo, { deep: true, immediate: true })

onMounted(() => {
  fetchOriginalBatchInfo()
})
</script>

<template>
  <div class="grid grid-cols-12 gap-x-20 gap-y-3">
    <div v-for="(roll, index) in model" :key="index" class="col-span-12">
      <h3 class="font-medium">Roll {{ index + 1 }}:</h3>
      <FinishedEditRollListItem
          v-model="model![index]"
          @remove="removeRoll(index)"
          :editable="!disabled"
          :show-actions="showActions"
          :current-batch-number="currentBatchNumber"
          :show-origin-tags="isTrulyMergedBatch"
      />
    </div>

    <div class="col-span-12" v-if="allowAddRolls">
      <SplitButton
          size="small"
          outlined
          label="Add Roll"
          @click="addRoll"
          :model="addRollOptions"
      />
    </div>
  </div>
</template>

<style scoped></style>
